import React from 'react';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import type { CampaignScheduleSourceRecord } from '~/hooks/apis/offers/useGetSalesforceCampaignSchedules';

import { formatDateISO } from '~/services/TimeService';

import type { CampaignSchedulePreview } from '../Detail/OfferBuilder/DealInformation/tabs/campaignSchedules/types';

type Props = {
  error?: unknown;
  isError: boolean;
  isLoading: boolean;
  onClose: () => void;
  open: boolean;
  preview?: CampaignSchedulePreview;
  sourceRecords: Array<CampaignScheduleSourceRecord>;
};

function DateRangeRow({ label, range }: { label: string; range: { start: string; end: string } | null }) {
  return (
    <TableRow>
      <TableCell component="th" scope="row">
        {label}
      </TableCell>
      <TableCell>{range ? formatDateISO(range.start) : '—'}</TableCell>
      <TableCell>{range ? formatDateISO(range.end) : '—'}</TableCell>
      <TableCell />
    </TableRow>
  );
}

export default function SalesforceCampaignSchedulesModal({
  error,
  isError,
  isLoading,
  onClose,
  open,
  preview,
  sourceRecords,
}: Props) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Import Schedules from Salesforce</DialogTitle>
      <DialogContent>
        {isLoading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error instanceof Error ? error.message : 'Failed to load Salesforce campaign schedules'}
          </Alert>
        )}

        {!isLoading && !isError && preview && (
          <Stack spacing={3}>
            {preview.warnings.length > 0 && (
              <Stack spacing={1}>
                {preview.warnings.map((warning) => (
                  <Alert key={warning} severity="warning">
                    {warning}
                  </Alert>
                ))}
              </Stack>
            )}

            <Box>
              <Typography variant="h6" gutterBottom>
                Derived schedule preview
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Start</TableCell>
                    <TableCell>End</TableCell>
                    <TableCell>Brand / Market</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <DateRangeRow label="Book By Dates (availability)" range={preview.availability} />
                  <DateRangeRow label="Available Offline (online_purchase)" range={preview.online_purchase} />
                  <DateRangeRow label="LuxPlus+" range={preview.lux_plus} />
                  {preview.list_visibility.length === 0 ? (
                    <TableRow>
                      <TableCell component="th" scope="row">
                        List Visibility
                      </TableCell>
                      <TableCell colSpan={3}>—</TableCell>
                    </TableRow>
                  ) : (
                    preview.list_visibility.map((row, index) => (
                      <TableRow key={`${row.brand}-${row.region}-${index}`}>
                        <TableCell component="th" scope="row">
                          {index === 0 ? 'List Visibility' : ''}
                        </TableCell>
                        <TableCell>{formatDateISO(row.start)}</TableCell>
                        <TableCell>{formatDateISO(row.end)}</TableCell>
                        <TableCell>
                          {row.brand} / {row.region}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>

            <Box>
              <Typography variant="h6" gutterBottom>
                Source Campaign_Schedule__c records ({sourceRecords.length})
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Campaign Type</TableCell>
                    <TableCell>Brand</TableCell>
                    <TableCell>Market</TableCell>
                    <TableCell>Start Offline</TableCell>
                    <TableCell>End Offline</TableCell>
                    <TableCell>Start Online</TableCell>
                    <TableCell>End Online</TableCell>
                    <TableCell>Synced At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sourceRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8}>No Campaign_Schedule__c records found for this offer</TableCell>
                    </TableRow>
                  ) : (
                    sourceRecords.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell>{record.dynamic_campaign_type__c || '—'}</TableCell>
                        <TableCell>{record.brand__c || '—'}</TableCell>
                        <TableCell>{record.market__c || '—'}</TableCell>
                        <TableCell>{record.start_offline__c ? formatDateISO(record.start_offline__c) : '—'}</TableCell>
                        <TableCell>{record.end_offline__c ? formatDateISO(record.end_offline__c) : '—'}</TableCell>
                        <TableCell>{record.start_online__c ? formatDateISO(record.start_online__c) : '—'}</TableCell>
                        <TableCell>{record.end_online__c ? formatDateISO(record.end_online__c) : '—'}</TableCell>
                        <TableCell>{record.synced_at ? formatDateISO(record.synced_at) : '—'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
